import * as THREE from 'three';
import SceneManager from './core/SceneManager';
import AudioManager from './audio/AudioManager';
import GuiManager from './gui/GuiManager';
import PostProcessor from './effects/PostProcessor';
import { SubtitleManager } from './subtitles/subtitleManager.js';

/**
 * Main application entry point.
 * Initializes all modules (Scene, Audio, GUI, PostProcessing)
 * and runs the main animation loop.
 */

console.log('main.js loaded');

// --- Global Variables & Parameters ---

/** @type {number} Normalized mouse X position (-1 to 1, approx) */
let mouseX = 0;
/** @type {number} Normalized mouse Y position (-1 to 1, approx) */
let mouseY = 0;

// Central object to hold parameters controllable by the GUI.
// This makes it easy to pass initial values and link GUI updates.
const effectParams = {
	red: 1.0,       // Initial red color component for the shader
	green: 1.0,     // Initial green color component
	blue: 1.0,      // Initial blue color component
	threshold: 0.3, // Adjusted bloom effect threshold
	strength: 0.25,  // Adjusted bloom effect strength
	radius: 0.8,    // Initial bloom effect radius
	visualEffect: 'icosahedron' // Change default to icosahedron
};

// --- Module Instances ---
// Declare module variables in the outer scope
/** @type {SceneManager | null} */
let sceneManager = null;
/** @type {AudioManager | null} */
let audioManager = null;
/** @type {GuiManager | null} */
let guiManager = null;
/** @type {PostProcessor | null} */
let postProcessor = null;
/** @type {SubtitleManager | null} */
let subtitleManager = null;
/** @type {THREE.Clock} Used for getting delta time and elapsed time */
const clock = new THREE.Clock();

// --- Initialization Function ---
/**
 * Initializes all application modules and starts the animation loop.
 */
function init() {
    // 1. Initialize Core Scene
    sceneManager = new SceneManager(); 
    
    // 2. Initialize Audio (needs the camera from SceneManager for the listener)
    audioManager = new AudioManager(sceneManager.getCamera()); 

    // 🎵 Default audio configuration
    // 设置为 false 可以禁用默认音频，避免干扰用户上传的内容
    const ENABLE_DEFAULT_AUDIO = false; // 👈 更改为 false 来禁用默认音频
    
    if (ENABLE_DEFAULT_AUDIO) {
        console.log('🎵 尝试加载默认演示音频...');
        audioManager.loadAndPlayUrl('assets/multilingual_greetings.mp3')
            .then(() => {
                console.log('✅ 默认音频加载成功（演示模式）');
                // Update GUI after default audio loads
                setTimeout(() => { // Use setTimeout as a simple delay
                    const duration = audioManager.getDuration();
                    if (guiManager && duration > 0) {
                        guiManager.updateSeekBarDuration(duration);
                        console.log(`🎵 默认音频时长: ${duration.toFixed(2)}秒`);
                    }
                }, 500); // Adjust delay as needed
            })
            .catch(err => {
                console.warn('⚠️ 默认音频加载失败:', err.message);
            });
    } else {
        console.log('🔇 默认音频已禁用，等待用户上传内容...');
    }
    
    // 3. Initialize Post-Processing (needs renderer, scene, camera, and initial params)
    postProcessor = new PostProcessor(
        sceneManager.getRenderer(), 
        sceneManager.getScene(), 
        sceneManager.getCamera(),
        {
            threshold: effectParams.threshold,
            strength: effectParams.strength,
            radius: effectParams.radius
        } // Pass initial bloom params
    );

    // 4. Initialize Subtitle Manager (before GUI, after Audio)
    const subtitleContainer = document.getElementById('subtitle-container');
    if (!subtitleContainer) {
        console.error('FATAL: Subtitle container element #subtitle-container not found!');
        // Handle error appropriately - maybe stop initialization?
    } else if (!audioManager) {
         console.error('FATAL: AudioManager not initialized before SubtitleManager!');
    } else {
        subtitleManager = new SubtitleManager(audioManager, subtitleContainer);
        console.log('SubtitleManager initialized.');
    }

    // 5. Initialize GUI (Order changed)
    // Define callback functions that GuiManager will call when sliders change.
    const guiCallbacks = {
        /** Updates shader color uniforms via SceneManager */
        onColorChange: (colorParams) => {
            if (sceneManager) {
                 sceneManager.updateShaderUniforms(colorParams);
            }
        },
        /** Updates bloom effect parameters via PostProcessor */
        onBloomChange: (bloomParams) => {
            if (postProcessor) {
                 postProcessor.updateParams(bloomParams);
            }
        },
        /** Switches the active visual effect via SceneManager */
        onEffectChange: (effectName) => {
            console.log('Switching effect to:', effectName);
            if (sceneManager) {
                sceneManager.setActiveEffect(effectName); 
            }
        },
        /** Triggers the microphone input start */
        onMicInputRequest: () => {
            // Call startMicrophoneInput on the audioManager instance
            // Use optional chaining and an arrow function to preserve `this` context implicitly
            if (audioManager) {
                audioManager.startMicrophoneInput(); 
            }
        },
        // +++ Add callback for combined media file upload +++
        onMediaUploadRequest: (files) => {
            let audioFile = null;
            let subtitleFile = null;

            // Clear previous state first when new files are uploaded
            console.log("🔄 正在处理新的媒体文件上传...");
            
            // Iterate through the selected files to identify audio and subtitle
            for (const file of files) {
                if (file.type.startsWith('audio/')) {
                    if (!audioFile) {
                        audioFile = file;
                    } else {
                        console.warn("检测到多个音频文件，使用第一个找到的文件。");
                    }
                } else if (file.name.toLowerCase().endsWith('.srt')) {
                    if (!subtitleFile) {
                        subtitleFile = file;
                    } else {
                        console.warn("检测到多个字幕文件，使用第一个找到的文件。");
                    }
                }
            }

            // Clear previous subtitles if new upload contains subtitle or audio
            if ((subtitleFile || audioFile) && subtitleManager) {
                console.log("🧹 清理之前的字幕状态...");
                subtitleManager.clearAll(); // Use the new clearAll method
            }

            // Process audio file
            if (audioFile && audioManager) {
                console.log("🎵 正在处理音频文件:", audioFile.name);
                
                // 🔄 完全重置 AudioManager 状态，清理默认音频干扰
                console.log("🧹 清理默认音频和之前的状态...");
                
                // Stop any existing audio first
                if (audioManager.isPlaying()) {
                    audioManager.sound.stop();
                }
                
                // 强制重置所有时间跟踪变量
                audioManager._resetPlaybackTimeTracking();
                
                // 清理分析器状态
                if (audioManager.audioAnalyser) {
                    try {
                        if (audioManager.audioAnalyser.analyser) {
                            audioManager.audioAnalyser.analyser.disconnect();
                        }
                    } catch (e) {
                        console.warn("清理分析器时出错:", e);
                    }
                    audioManager.audioAnalyser = null;
                }
                
                audioManager.loadAndPlayFile(audioFile);
                // Delay duration update as before
                setTimeout(() => {
                    const duration = audioManager.getDuration();
                    if (guiManager && duration > 0) {
                        guiManager.updateSeekBarDuration(duration);
                        console.log(`✅ 音频加载成功，时长: ${duration.toFixed(2)}秒`);
                        
                        // 🎯 强制重置进度条到0
                        guiManager.updateSeekBarValue(0);
                        console.log("🔄 进度条已重置到开始位置");
                    }
                }, 500);
            } else if (files.length > 0 && !audioFile) {
                 console.log("ℹ️ 未选择音频文件或AudioManager未就绪。");
                 // Alert only if files were selected but none were audio
                 if (Array.from(files).some(f => !f.name.toLowerCase().endsWith('.srt'))) {
                     alert("请选择一个有效的音频文件 (例如 .mp3, .wav)！");
                 }
            }

            // Process subtitle file
            if (subtitleFile && subtitleManager) {
                 console.log("📝 正在处理字幕文件:", subtitleFile.name);
                 const reader = new FileReader();
                 reader.onload = (e) => {
                     const srtContent = e.target.result;
                     subtitleManager.loadSubtitlesFromString(srtContent);
                     console.log(`✅ 字幕加载成功: ${subtitleFile.name}`);
                 };
                 reader.onerror = (err) => {
                     console.error("❌ 读取字幕文件失败:", err);
                     alert("无法读取字幕文件！");
                 };
                 reader.readAsText(subtitleFile);
            } else if (files.length > 0 && !subtitleFile) {
                 console.log("ℹ️ 未选择字幕文件或SubtitleManager未就绪。");
            }

            // Provide user feedback
            if (audioFile && subtitleFile) {
                console.log("🎯 音频和字幕文件已同时上传，将自动同步播放。");
            } else if (subtitleFile && !audioFile) {
                console.warn("⚠️ 仅上传了字幕文件，请同时上传音频文件以确保同步。");
            } else if (audioFile && !subtitleFile) {
                console.log("🎵 仅上传了音频文件，将播放无字幕版本。");
            }

            if (!audioFile && !subtitleFile && files.length > 0) {
                 console.warn("❌ 未在选择中找到有效的音频或SRT字幕文件。");
                 alert("未在选择中找到有效的音频或 SRT 字幕文件。");
            }
        }
    };
    // Create the GUI, passing the initial parameters, the callbacks, and the audio manager
    guiManager = new GuiManager(effectParams, guiCallbacks, audioManager, subtitleManager);

    // Explicitly set the initial effect based on params
    if (sceneManager) {
        sceneManager.setActiveEffect(effectParams.visualEffect);
    }

    // 6. Setup Event Listeners
    setupEventListeners();
    
    // 7. Start the Animation Loop (Order changed)
    startAnimationLoop(); 
    console.log('Initialization complete.');
}

// --- Event Listeners Setup ---
/**
 * Sets up global event listeners (window resize, mouse move).
 */
function setupEventListeners() {
    window.addEventListener('resize', onWindowResize);
    document.addEventListener('mousemove', onMouseMove);
    // Add touch event listener for mobile compatibility
    document.addEventListener('touchmove', onTouchMove, { passive: false }); 
}

/**
 * Handles window resize events.
 * Notifies relevant modules (SceneManager, PostProcessor) to update their sizes.
 */
function onWindowResize() {
    console.log('Window resized');
    if (sceneManager) {
        sceneManager.onResize(); // Updates camera aspect, renderer size
    }
    if (postProcessor) {
        postProcessor.onResize(); // Updates composer size
    }
}

/**
 * Handles mouse movement events.
 * Updates normalized mouse coordinates (mouseX, mouseY).
 * @param {MouseEvent} event
 */
function onMouseMove(event) {
    // Convert mouse position to normalized device coordinates (-1 to +1 range approx)
    const windowHalfX = window.innerWidth / 2;
    const windowHalfY = window.innerHeight / 2;
    mouseX = (event.clientX - windowHalfX) / windowHalfX; // Normalize X
    mouseY = (event.clientY - windowHalfY) / windowHalfY; // Normalize Y (inverted for typical 3D coordinate systems)
    // Adjust sensitivity/scaling if needed: e.g., mouseX /= 2;
}

/**
 * Handles touch movement events on mobile devices.
 * Updates normalized mouse coordinates (mouseX, mouseY) based on the first touch point.
 * Prevents default scroll behavior.
 * @param {TouchEvent} event
 */
function onTouchMove(event) {
    // Prevent the default touch action (like scrolling)
    event.preventDefault(); 

    if (event.touches.length > 0) {
        const touch = event.touches[0]; // Get the first touch point
        const windowHalfX = window.innerWidth / 2;
        const windowHalfY = window.innerHeight / 2;
        mouseX = (touch.clientX - windowHalfX) / windowHalfX; // Normalize X
        mouseY = (touch.clientY - windowHalfY) / windowHalfY; // Normalize Y 
    }
}

// --- Animation Loop ---
/**
 * Starts the main animation loop using requestAnimationFrame.
 */
function startAnimationLoop() {
    /**
     * The main animation loop function.
     * Called recursively via requestAnimationFrame.
     */
    function animate() {
        requestAnimationFrame(animate); // Schedule the next frame

        const deltaTime = clock.getDelta(); // Time since last frame
        const elapsedTime = clock.getElapsedTime(); // Total time elapsed

        // --- Update Modules ---
        let audioFrequency = 0;
        if (audioManager) {
            // Update audio analysis data (if needed by effects)
            audioFrequency = audioManager.getAverageFrequency();

            // +++ Update Playback GUI +++
            if (guiManager && audioManager.sound && audioManager.sound.buffer) { // Check if audio is ready
                const currentTime = audioManager.getCurrentPlaybackTime();
                guiManager.updateSeekBarValue(currentTime);

                // 🐛 DEBUG: Log time values occasionally for debugging
                if (Math.random() < 0.001) { // Very occasional logging (0.1% of frames)
                    console.log(`⏱️ Debug time values:`, {
                        currentTime: currentTime.toFixed(3),
                        isPlaying: audioManager.isPlaying(),
                        duration: audioManager.getDuration().toFixed(3)
                    });
                }

                // Check if playback naturally ended
                if (audioManager.isPlaying() && currentTime >= audioManager.getDuration() - 0.1) { // Small buffer
                    console.log("Playback likely finished.");
                    // We might want to visually reset the play button here if possible
                    // or trigger a pause state.
                    // For now, the currentTime will just stop updating.
                }
            }
        }

        if (sceneManager) {
            // Update scene elements (e.g., camera position based on mouse)
            sceneManager.update(deltaTime, elapsedTime, { mouseX, mouseY });
            // Pass time and audio data to update shader uniforms
            sceneManager.updateShaderUniforms({ u_time: elapsedTime, u_frequency: audioFrequency });
        }
        // AudioManager might have internal updates if needed (e.g., smoothing audio data)
        // if (audioManager) audioManager.update(deltaTime);
        // GuiManager typically doesn't need updates within the loop

        // +++ Update Subtitle Manager +++
        if (subtitleManager) {
            subtitleManager.update(); // Update subtitles based on audio time
        }

        // --- Render --- 
        // Render the scene through the post-processing pipeline
        if (postProcessor) {
            postProcessor.render(); 
        } else if (sceneManager) {
            // Fallback: Render directly if post-processor failed to initialize
            sceneManager.getRenderer().render(sceneManager.getScene(), sceneManager.getCamera());
        }
    }
    animate(); // Start the loop
}

// --- Start the application --- 
// Ensure DOM is ready or run after DOMContentLoaded if necessary, though modules handle DOM appending.
init(); 